// Updated src/components/Footer.jsx with Instagram logo image and tag suggestion
import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";

const Footer = ({ recentGuiltScore }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const coffeeControls = useAnimation();
  
  // Effect to occasionally animate the coffee button
  useEffect(() => {
    const animateCoffee = () => {
      // Only animate occasionally (15% chance each interval)
      if (Math.random() < 0.15) {
        coffeeControls.start({
          rotate: [0, -5, 5, -5, 5, 0],
          transition: { duration: 0.6 }
        });
      }
    };
    
    // Set up interval for occasional animations - every 30-60 seconds
    const randomInterval = Math.floor(Math.random() * 30000) + 30000; // 30-60 seconds
    const intervalId = setInterval(animateCoffee, randomInterval);
    
    return () => clearInterval(intervalId);
  }, [coffeeControls]);
  
  // Effect to show prompt when guilt score is low
  useEffect(() => {
    if (recentGuiltScore !== undefined && recentGuiltScore < 30) {
      // Show the prompt briefly when a low guilt score happens
      setShowPrompt(true);
      const timerId = setTimeout(() => setShowPrompt(false), 5000); // Hide after 5 seconds
      return () => clearTimeout(timerId);
    }
  }, [recentGuiltScore]);
  
  return (
    <footer className="w-full max-w-md mx-auto text-center text-xs text-gray-500 py-6 relative">
      <div>
        Built with 💸 by <span className="font-semibold">GuiltGauge.com</span>
      </div>
      <div className="mt-1">
        <span className="italic">It's called retail therapy for a reason.</span>
      </div>
      
      {/* Social Media Links */}
      <div className="mt-3 flex justify-center space-x-4">
        <motion.a 
          href="https://instagram.com/guiltgauge/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium text-xs hover:opacity-90 transition-opacity"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/images/insta.png" alt="Instagram" className="w-4 h-4 mr-1.5" />
          Follow on Instagram
        </motion.a>
        
        <motion.a 
          href="https://buymeacoffee.com/theinternetjanitor" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 py-1.5 bg-yellow-400 text-yellow-800 rounded-lg font-medium text-xs hover:bg-yellow-500 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          animate={coffeeControls}
          onHoverStart={() => {
            // Animate on hover too
            coffeeControls.start({
              rotate: [0, -5, 5, -5, 5, 0],
              transition: { duration: 0.6 }
            });
          }}
        >
          <span className="mr-1.5">☕</span>
          Buy me a coffee
        </motion.a>
      </div>
      
      {/* Instagram tag suggestion - only shows after user has viewed results */}
      {recentGuiltScore !== undefined && (
        <div className="mt-2 text-xs text-gray-400 animate-pulse">
          Share your shopping guilt result and tag @guiltgauge on Instagram!
        </div>
      )}
      
      {/* Prompt that appears on low guilt scores */}
      {showPrompt && (
        <motion.div 
          className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-xs shadow-md"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          Guilt-free shopping? Maybe buy the developer a coffee! 😉
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-yellow-100"></div>
        </motion.div>
      )}
      
      {/* Hidden easter egg */}
      <div className="mt-4 opacity-30 hover:opacity-100 transition-opacity">
        <button 
          onClick={() => alert("Money can't buy happiness, but it's more comfortable to cry in a designer outfit.")}
          className="text-xs underline"
        >
          Need shopping justification?
        </button>
      </div>

      {/* Mindful shop link */}
      <div className="mt-3 text-xs opacity-60 hover:opacity-100 transition-opacity">
        <a href="/shop" className="text-blue-500 hover:underline inline-flex items-center">
          <span className="mr-1">🛍️</span> Mindful shopping recommendations
        </a>
      </div>
    </footer>
  );
};

export default Footer;
